/**
 * simulateContactFlow.ts - Contact flow enrichment for editable canvas
 *
 * Purpose:
 *   Enriches canvas nodes/edges with contact visualization data.
 *
 * Behavior:
 *   - Uses API step-status data when available to place contacts on the exact path taken.
 *   - Falls back to no path highlighting when step data is missing.
 *   - Always clears stale contact data when no contacts are active.
 */

import { type Node, type Edge } from '@xyflow/react';
import { EDGE_TYPES } from '../types/edges';
import { type FlowContact } from '../types/contacts';

interface NodeWithContactData extends Record<string, any> {
	contactColors?: string[];
	contactCount?: number;
}

interface EdgeWithContactData extends Record<string, any> {
	activeContacts?: FlowContact[];
}

/** Minimal runtime shape needed from contact step-status API data. */
export interface ContactStepStatusLike {
	step?: {
		id?: string;
		_type?: string;
	};
	status?: {
		status?: string;
		outcome?: string;
	} & Record<string, unknown>;
}

const SWITCH_STEP_TYPE = 'SwitchAutomationStep';

function areStringArraysEqual(previous: string[] | undefined, next: string[]): boolean {
	if (!previous) {
		return next.length === 0;
	}
	if (previous.length !== next.length) {
		return false;
	}
	for (let i = 0; i < previous.length; i++) {
		if (previous[i] !== next[i]) {
			return false;
		}
	}
	return true;
}

export function areContactsEqualById(previous: FlowContact[] | undefined, next: FlowContact[]): boolean {
	if (!previous) {
		return next.length === 0;
	}
	if (previous.length !== next.length) {
		return false;
	}
	for (let i = 0; i < previous.length; i++) {
		if (previous[i]?.id !== next[i]?.id) {
			return false;
		}
	}
	return true;
}

function getStepNodeId(stepStatus: ContactStepStatusLike): string | null {
	const stepId = stepStatus.step?.id;
	if (!stepId) {
		return null;
	}
	return `step-${stepId}`;
}

function getSwitchOutcomeCaseId(stepStatus: ContactStepStatusLike): string | undefined {
	const outcome = stepStatus.status?.outcome;
	return typeof outcome === 'string' && outcome.trim().length > 0 ? outcome : undefined;
}

function findBranchEdgeForSwitch<T extends EdgeWithContactData>(
	edges: Edge<T>[],
	switchNodeId: string,
	outcomeCaseId?: string
): Edge<T> | undefined {
	const branchEdges = edges.filter(edge => {
		if (edge.type !== EDGE_TYPES.BRANCH) {
			return false;
		}
		const edgeData = edge.data as { switchNodeId?: string } | undefined;
		return edgeData?.switchNodeId === switchNodeId;
	});

	if (branchEdges.length === 0) {
		return undefined;
	}

	if (outcomeCaseId) {
		const matchedByOutcome = branchEdges.find(edge => {
			const edgeData = edge.data as { caseId?: string } | undefined;
			return edgeData?.caseId === outcomeCaseId;
		});
		if (matchedByOutcome) {
			return matchedByOutcome;
		}
	}

	const defaultEdge = branchEdges.find(edge => {
		const edgeData = edge.data as { isDefault?: boolean } | undefined;
		return edgeData?.isDefault === true;
	});

	return defaultEdge ?? branchEdges[0];
}

function collectTraversedEdgeIds<T extends EdgeWithContactData>(
	edges: Edge<T>[],
	steps: ContactStepStatusLike[]
): Set<string> {
	const traversedEdgeIds = new Set<string>();

	if (steps.length === 0) {
		return traversedEdgeIds;
	}

	const firstNonSwitchStep = steps.find(stepStatus => stepStatus.step?._type !== SWITCH_STEP_TYPE);
	if (firstNonSwitchStep) {
		const firstStepNodeId = getStepNodeId(firstNonSwitchStep);
		if (firstStepNodeId) {
			const incomingEdge =
				edges.find(edge => edge.source === 'trigger' && edge.target === firstStepNodeId) ??
				edges.find(edge => edge.target === firstStepNodeId);

			if (incomingEdge) {
				traversedEdgeIds.add(incomingEdge.id);
			}
		}
	}

	for (let i = 0; i < steps.length - 1; i++) {
		const currentStep = steps[i];
		const nextStep = steps[i + 1];

		if (currentStep.step?._type === SWITCH_STEP_TYPE) {
			const switchNodeId = getStepNodeId(currentStep);
			if (!switchNodeId) {
				continue;
			}

			const outcomeCaseId = getSwitchOutcomeCaseId(currentStep);
			const branchEdge = findBranchEdgeForSwitch(edges, switchNodeId, outcomeCaseId);

			if (!branchEdge) {
				continue;
			}

			traversedEdgeIds.add(branchEdge.id);

			const nextBranchStepNodeId = getStepNodeId(nextStep);
			if (!nextBranchStepNodeId) {
				continue;
			}

			const segmentToNextEdge = edges.find(
				edge => edge.source === branchEdge.target && edge.target === nextBranchStepNodeId
			);
			if (segmentToNextEdge) {
				traversedEdgeIds.add(segmentToNextEdge.id);
			}

			continue;
		}

		if (nextStep.step?._type === SWITCH_STEP_TYPE) {
			continue;
		}

		const currentStepNodeId = getStepNodeId(currentStep);
		const nextStepNodeId = getStepNodeId(nextStep);
		if (!currentStepNodeId || !nextStepNodeId) {
			continue;
		}

		const directEdge = edges.find(edge => edge.source === currentStepNodeId && edge.target === nextStepNodeId);
		if (directEdge) {
			traversedEdgeIds.add(directEdge.id);
		}
	}

	const lastStep = steps[steps.length - 1];
	if (lastStep?.step?._type === SWITCH_STEP_TYPE) {
		const switchNodeId = getStepNodeId(lastStep);
		if (switchNodeId) {
			const outcomeCaseId = getSwitchOutcomeCaseId(lastStep);
			const branchEdge = findBranchEdgeForSwitch(edges, switchNodeId, outcomeCaseId);
			if (branchEdge) {
				traversedEdgeIds.add(branchEdge.id);
			}
		}
	}

	return traversedEdgeIds;
}

/**
 * Enriches nodes with contact distribution from known step-status data.
 */
export function enrichNodesWithSimulatedContacts<T extends NodeWithContactData>(
	nodes: Node<T>[],
	activeContacts: FlowContact[],
	contactStepsByContactId?: Map<string, ContactStepStatusLike[] | undefined>
): Node<T>[] {
	if (activeContacts.length === 0) {
		let didClearNodeContacts = false;
		const clearedNodes = nodes.map(node => {
			if (node.id === 'trigger') {
				return node;
			}
			const existingColors = (node.data as NodeWithContactData).contactColors || [];
			const existingCount = (node.data as NodeWithContactData).contactCount || 0;
			if (existingColors.length === 0 && existingCount === 0) {
				return node;
			}
			didClearNodeContacts = true;
			return {
				...node,
				data: {
					...node.data,
					contactColors: [],
					contactCount: 0,
				},
			};
		});
		return didClearNodeContacts ? clearedNodes : nodes;
	}

	const contactsById = new Map(activeContacts.map(contact => [contact.id, contact]));
	const contactIdsByNodeId = new Map<string, Set<string>>();

	for (const contact of activeContacts) {
		const contactSteps = contactStepsByContactId?.get(contact.id) ?? [];
		if (!contactSteps || contactSteps.length === 0) {
			continue;
		}

		for (const stepStatus of contactSteps) {
			if (stepStatus.step?._type === SWITCH_STEP_TYPE) {
				continue;
			}
			const nodeId = getStepNodeId(stepStatus);
			if (!nodeId) {
				continue;
			}
			if (!contactIdsByNodeId.has(nodeId)) {
				contactIdsByNodeId.set(nodeId, new Set<string>());
			}
			contactIdsByNodeId.get(nodeId)?.add(contact.id);
		}
	}

	let changed = false;
	const enrichedNodes = nodes.map(node => {
		if (node.id === 'trigger') {
			return node;
		}

		const contactIds = [...(contactIdsByNodeId.get(node.id) ?? [])];
		const contactColors = contactIds
			.map(contactId => contactsById.get(contactId)?.color)
			.filter((color): color is string => Boolean(color));

		const existingColors = (node.data as NodeWithContactData).contactColors;
		const existingCountValue = (node.data as NodeWithContactData).contactCount;
		const existingCount = typeof existingCountValue === 'number' ? existingCountValue : undefined;
		if (areStringArraysEqual(existingColors, contactColors) && existingCount === contactColors.length) {
			return node;
		}

		changed = true;
		return {
			...node,
			data: {
				...node.data,
				contactColors,
				contactCount: contactColors.length,
			},
		};
	});

	return changed ? enrichedNodes : nodes;
}

/**
 * Enriches edges with contact animation data based on actual contact step progression.
 */
export function enrichEdgesWithSimulatedContacts<T extends EdgeWithContactData>(
	nodes: Node[],
	edges: Edge<T>[],
	activeContacts: FlowContact[],
	contactStepsByContactId?: Map<string, ContactStepStatusLike[] | undefined>
): Edge<T>[] {
	if (activeContacts.length === 0) {
		let didClearEdgeContacts = false;
		const clearedEdges = edges.map(edge => {
			const existingContacts = (edge.data as EdgeWithContactData | undefined)?.activeContacts || [];
			if (existingContacts.length === 0) {
				return edge;
			}
			didClearEdgeContacts = true;
			return {
				...edge,
				data: {
					...edge.data,
					activeContacts: [],
				},
			};
		});
		return didClearEdgeContacts ? clearedEdges : edges;
	}

	const nodeIds = new Set(nodes.map(node => node.id));
	const flowEdges = edges.filter(
		edge => edge.type !== EDGE_TYPES.PHANTOM_ADD_PATH && nodeIds.has(edge.source) && nodeIds.has(edge.target)
	);

	const contactsById = new Map(activeContacts.map(contact => [contact.id, contact]));
	const contactIdsByEdgeId = new Map<string, Set<string>>();

	for (const contact of activeContacts) {
		const contactSteps = contactStepsByContactId?.get(contact.id) ?? [];
		if (!contactSteps || contactSteps.length === 0) {
			continue;
		}

		const traversedEdgeIds = collectTraversedEdgeIds(flowEdges, contactSteps);
		for (const edgeId of traversedEdgeIds) {
			if (!contactIdsByEdgeId.has(edgeId)) {
				contactIdsByEdgeId.set(edgeId, new Set<string>());
			}
			contactIdsByEdgeId.get(edgeId)?.add(contact.id);
		}
	}

	let changed = false;
	const enrichedEdges = edges.map(edge => {
		const contactIds = [...(contactIdsByEdgeId.get(edge.id) ?? [])];
		const contactsForEdge = contactIds
			.map(contactId => contactsById.get(contactId))
			.filter((contact): contact is FlowContact => Boolean(contact));

		const existingContacts = (edge.data as EdgeWithContactData | undefined)?.activeContacts;
		if (areContactsEqualById(existingContacts, contactsForEdge)) {
			return edge;
		}

		changed = true;
		return {
			...edge,
			data: {
				...edge.data,
				activeContacts: contactsForEdge,
			},
		};
	});

	return changed ? enrichedEdges : edges;
}
